import { useCallback } from 'react';
import { useInstitutionsStore } from '@/stores/institutions';
import { useOrganizationsStore } from '@/stores/organizations';
import { useUsersStore } from '@/stores/users';
import { redirectToInstitution } from '@/utils/institutionsUtils';

/**
 * Returns a function that switches the current organization: it updates the
 * organizations store, persists the choice as the user's last-utilized
 * organization, and cascades into the organization's first institution
 * (in-place if it's on the current subdomain, otherwise via a full-page
 * redirect to the login route).
 * @returns Organization change handler accepting an organization ID.
 */
export const useOrganizationChange = () => {
  const setLastUtilizedOrganization = useUsersStore(
    (state) => state.setLastUtilizedOrganization,
  );

  const handleOrganizationChange = useCallback(
    async (id: string) => {
      // Read fresh state via getState() rather than a reactive selector:
      // this is called right after async fetches (e.g. getOrganizations),
      // and a selector-captured closure would still see pre-fetch data.
      const { organizations, currentOrganization, setCurrentOrganizationById } =
        useOrganizationsStore.getState();

      if (id === currentOrganization?.id) return;

      const organizationExists = organizations.some((org) => org.id === id);

      if (!organizationExists) {
        console.error(`Organization with ID "${id}" not found.`);
        return;
      }

      setCurrentOrganizationById(id);

      // Awaited rather than fire-and-forget: a cross-subdomain switch below
      // triggers a hard page navigation, which can abort this request
      // before it reaches the backend if it isn't awaited first. That
      // leaves last-utilized-organization stale, so the next load bounces
      // back to the organization we're switching away from right now.
      try {
        await setLastUtilizedOrganization(id);
      } catch (error) {
        console.error('Failed to persist last utilized organization:', error);
      }

      const {
        institutions,
        currentInstitution,
        setCurrentInstitutionById,
        clearCurrentInstitution,
      } = useInstitutionsStore.getState();

      const [firstInstitution] = institutions
        .filter((institution) => institution.organizationId === id)
        .sort((a, b) => a.name.localeCompare(b.name));

      if (!firstInstitution) {
        clearCurrentInstitution();
        return;
      }

      if (currentInstitution && currentInstitution.id !== firstInstitution.id) {
        redirectToInstitution({
          currentInstitution,
          targetInstitution: firstInstitution,
        });
        return;
      }
      setCurrentInstitutionById(firstInstitution.id);
    },
    [setLastUtilizedOrganization],
  );

  return handleOrganizationChange;
};
